import React, { useState } from 'react'
import webSocket from 'services/api/webSocket'
import { getMarkeyDataWsUrl } from 'utils/urlBuilder'

import GeneralAlert from '../App/GeneralAlert'
import useWS from '../App/useWS'
import { Book, BookSide, MarketDeltaType, OrderBookEntryType } from './types'

// Mutations, Map and side effects are used for performance reasons
const updateBook = (
  data: BookSide,
  update: OrderBookEntryType[]
): Map<number, OrderBookEntryType> => {
  for (let i = 0; i < update.length; i += 1) {
    const existing = data.has(update[i].px)

    if (existing && update[i].qty === 0) {
      data.delete(update[i].px)
      continue
    }

    if (update[i].qty !== 0) {
      data.set(update[i].px, update[i])
    }
  }

  return data
}

type Context = {
  market: Book
  pair: string
  subscribe: (pair: string) => void
  unsubscribe: (pair: string) => void
}

export const MarketDataContext = React.createContext<Context>({
  market: {
    asks: new Map(),
    bids: new Map()
  },
  pair: '',
  subscribe: () => {},
  unsubscribe: () => {}
})

const MarketData = ({ children }) => {
  const [market, setMarket] = useState<Book>({ asks: new Map(), bids: new Map() })
  const [currentPair, setCurrentPair] = useState('')

  const { error, ready, ws } = useWS(getMarkeyDataWsUrl(), {
    onMessage: (message) => {
      if (message.channel !== 'l2') return

      if (message.event === webSocket.event.REJECTED) {
        // eslint-disable-next-line no-console
        console.warn('Market data WS rejection', message)
        return
      }

      if (message.event === webSocket.event.SNAPSHOT) {
        const eventMessage = message as MarketDeltaType
        setMarket({
          asks: updateBook(new Map(), eventMessage.asks),
          bids: updateBook(new Map(), eventMessage.bids)
        })
        return
      }

      if (message.event === webSocket.event.UPDATED) {
        const eventMessage = message as MarketDeltaType
        setMarket((data) => ({
          asks: updateBook(data.asks, eventMessage.asks),
          bids: updateBook(data.bids, eventMessage.bids)
        }))
        return
      }

      if (message.event === webSocket.event.SUBSCRIBED) {
        setCurrentPair(message.symbol)
        return
      }

      if (message.event === webSocket.event.UNSUBSCRIBED) {
        // Clear book
        setMarket({ asks: new Map(), bids: new Map() })
      }
    }
  })

  const context: Context = {
    market,
    pair: currentPair,
    subscribe: (pair: string) => {
      if (!ws || [ws.CLOSED, ws.CLOSING].includes(ws.readyState)) return

      ws?.send(
        JSON.stringify({
          action: webSocket.action.SUBSCRIBE,
          channel: webSocket.channel.MARKET,
          interval_type: 'fast',
          symbol: pair
        })
      )
    },
    unsubscribe: (pair: string) => {
      if (!ws || [ws.CLOSED, ws.CLOSING].includes(ws.readyState)) return

      ws.send(
        JSON.stringify({
          action: webSocket.action.UNSUBSCRIBE,
          channel: webSocket.channel.MARKET,
          interval_type: 'fast',
          symbol: pair
        })
      )
    }
  }

  if (error) return <GeneralAlert code='WS-002' />

  if (!ready) return null

  return <MarketDataContext.Provider value={context}>{children}</MarketDataContext.Provider>
}

export default MarketData
